import { useState, useEffect } from 'react';
import {
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw, ChevronDown, Check,
  DollarSign, Activity, BarChart2, Clock, Shield, Zap, Globe, Lock,
  TrendingUp, TrendingDown, X, AlertTriangle, Settings
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import {
  getExchangeConnections, getExchangeBalance, getAllExchangeBalances,
  placeExchangeOrder, getExchangeOpenOrders, cancelExchangeOrder,
  getExchangeTradeHistory, getExchangeMarkets, getExchangeTicker,
} from '../services/api';

// Sensible default pair list — overwritten when the exchange's real markets load.
// This is what the dropdown shows during the brief load before the first
// getExchangeMarkets() call completes.
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Sorts pairs so popular / quote-USDT / quote-USD ones surface first.
// Real-time alphabetical sort under that for predictability.
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    // Lower number = higher priority. Unknown bases/quotes pushed to the end.
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

export default function CexTradingPage() {
  const { user } = useAuth();
  const [exchanges, setExchanges] = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trade'); // trade | balances | orders | history

  // Balances
  const [allBalances, setAllBalances] = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  // Order form
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [placing, setPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Open orders
  const [openOrders, setOpenOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Trade history
  const [tradeHistory, setTradeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Live ticker for the selected pair — used to convert ETH ↔ USDC for the user.
  // New crypto users can't mentally compute "0.001 ETH × $2,283 = $2.28" while
  // typing, so we show the dollar value live under the Amount field.
  const [tickerPrice, setTickerPrice] = useState(null);
  const [tickerLoading, setTickerLoading] = useState(false);
  // Toggle: enter amount in BASE (ETH) or QUOTE (USDC). Default base.
  const [amountMode, setAmountMode] = useState('base'); // 'base' | 'quote'

  useEffect(() => { loadExchanges(); }, []);
  useEffect(() => {
    if (activeTab === 'balances' && !allBalances) loadAllBalances();
    if (activeTab === 'orders' && selectedExchange) loadOpenOrders();
    if (activeTab === 'history' && selectedExchange) loadHistory();
  }, [activeTab, selectedExchange]);

  // Load real markets whenever the user switches exchanges. Each exchange has
  // its own pair list — Binance US has different pairs than Coinbase. Cached
  // 1 hour per exchange on the backend, so repeated switches are cheap.
  useEffect(() => {
    if (!selectedExchange) return;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(selectedExchange)
      .then(res => {
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) {
          setPairs(FALLBACK_PAIRS);
          return;
        }
        const sorted = sortPairs(all);
        setPairs(sorted);
        // If the previously selected symbol doesn't exist on this exchange,
        // jump to the first available pair so the order form stays usable.
        if (!sorted.includes(symbol)) setSymbol(sorted[0]);
      })
      .catch(() => setPairs(FALLBACK_PAIRS))
      .finally(() => setPairsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExchange]);

  // Filtered list for the searchable dropdown — case-insensitive substring match.
  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  // Live ticker for the selected pair: fetch immediately when symbol or
  // exchange changes, then refresh every 15s while user stays on the trade tab.
  // We reset the price to null on change so the UI shows "Loading price…"
  // instead of stale numbers from the previous pair.
  useEffect(() => {
    if (!selectedExchange || !symbol || activeTab !== 'trade') return;
    let cancelled = false;
    setTickerPrice(null);
    setTickerLoading(true);

    async function refresh() {
      try {
        const t = await getExchangeTicker(selectedExchange, symbol);
        if (!cancelled && t?.last && t.last > 0) setTickerPrice(t.last);
      } catch { /* exchange may not have this pair or rate-limited — leave null */ }
      finally { if (!cancelled) setTickerLoading(false); }
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedExchange, symbol, activeTab]);

  // Format helpers for the live conversion line. Stablecoins (USDT/USDC/USD)
  // get 2 decimals; high-priced assets (BTC, ETH) get 4; everything else gets
  // 6. This matches what users intuitively expect when looking at totals.
  const [base, quote] = (symbol || '/').split('/');
  function fmtQuote(n) {
    if (!Number.isFinite(n)) return '—';
    if (['USDT', 'USDC', 'USD', 'BUSD', 'DAI'].includes(quote)) return n.toFixed(2);
    if (n >= 100) return n.toFixed(2);
    if (n >= 1)   return n.toFixed(4);
    return n.toFixed(6);
  }
  function fmtBase(n) {
    if (!Number.isFinite(n)) return '—';
    if (n >= 1)    return n.toFixed(4);
    if (n >= 0.01) return n.toFixed(6);
    return n.toFixed(8);
  }

  // Compute the "other side" of the trade based on amountMode + price.
  // amountMode='base'  → user typed ETH amount → show USDC total
  // amountMode='quote' → user typed USDC amount → show ETH amount
  const amountNum = parseFloat(amount) || 0;
  const refPrice = orderType === 'limit' && price ? parseFloat(price) : tickerPrice;
  const baseAmount  = amountMode === 'base'  ? amountNum : (refPrice ? amountNum / refPrice : 0);
  const quoteAmount = amountMode === 'quote' ? amountNum : (refPrice ? amountNum * refPrice : 0);

  async function loadExchanges() {
    try {
      const conn = await getExchangeConnections();
      const connected = (conn.exchanges || []).filter(e => e.connected);
      setExchanges(connected);
      if (connected.length > 0) setSelectedExchange(connected[0].exchangeId);
    } catch {} finally { setLoading(false); }
  }

  async function loadAllBalances() {
    setBalLoading(true);
    try { setAllBalances(await getAllExchangeBalances()); } catch {}
    setBalLoading(false);
  }

  async function loadOpenOrders() {
    if (!selectedExchange) return;
    setOrdersLoading(true);
    try { const d = await getExchangeOpenOrders(selectedExchange); setOpenOrders(d.orders || []); } catch { setOpenOrders([]); }
    setOrdersLoading(false);
  }

  async function loadHistory() {
    if (!selectedExchange) return;
    setHistoryLoading(true);
    try { const d = await getExchangeTradeHistory(selectedExchange); setTradeHistory(d.trades || []); } catch { setTradeHistory([]); }
    setHistoryLoading(false);
  }

  async function handlePlaceOrder() {
    if (!amount || parseFloat(amount) <= 0) return;
    if (amountMode === 'quote' && (!refPrice || refPrice <= 0)) {
      setOrderResult({ error: 'No live price for this pair. Switch back to base-amount mode or refresh.' });
      return;
    }
    const typedAmount = parseFloat(amount);
    // CCXT createOrder normally takes BASE amount. We also pass the explicit
    // quote (USDC) amount when the user typed in quote-mode so the backend can
    // use it directly for exchanges that require quote-sized market buys
    // (Coinbase Advanced Trade is the main example).
    const submitAmount = amountMode === 'base'
      ? typedAmount
      : typedAmount / refPrice; // base amount (estimated from ticker)
    const costQuote = amountMode === 'quote' ? typedAmount : null;

    setPlacing(true); setOrderResult(null);
    try {
      const result = await placeExchangeOrder(
        selectedExchange, symbol, side, orderType,
        submitAmount,
        orderType === 'limit' ? parseFloat(price) : null,
        {},
        costQuote,
      );
      setOrderResult(result);
      if (activeTab === 'orders') loadOpenOrders();
    } catch (err) { setOrderResult({ error: err.message }); }
    setPlacing(false);
  }

  async function handleCancelOrder(orderId) {
    try { await cancelExchangeOrder(selectedExchange, orderId, symbol); loadOpenOrders(); } catch (err) { alert(err.message); }
  }

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  if (exchanges.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-400/10 flex items-center justify-center mx-auto mb-4"><Globe className="w-8 h-8 text-amber-400" /></div>
          <h2 className="text-xl font-bold text-white mb-2">CEX Trading</h2>
          <p className="text-gray-400 mb-4">Connect an exchange in Settings → Connections to start trading on 15 centralized exchanges.</p>
          <p className="text-gray-500 text-xs mb-4">Supported: Binance, Coinbase, Kraken, Bybit, OKX, KuCoin, Gate.io, Bitget, HTX, and more</p>
          <a href="/settings?tab=connections" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm hover:bg-amber-400 transition-all">Go to Settings → Connections</a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center"><Activity className="w-5 h-5 text-white" /></div>
            CEX Trading
          </h1>
          <p className="text-gray-500 text-sm mt-1">Trade on {exchanges.length} connected exchanges — unified balance, orders, and history</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab !== 'balances' && (
            <>
              <span className="text-gray-500 text-xs">Trading on:</span>
              <select value={selectedExchange} onChange={e => setSelectedExchange(e.target.value)}
                className="bg-amber-400/10 border-2 border-amber-400/40 rounded-xl px-4 py-2.5 text-amber-400 text-sm font-bold focus:border-amber-500 focus:outline-none">
                {exchanges.map(ex => <option key={ex.exchangeId} value={ex.exchangeId} className="bg-dark-800 text-white">{ex.name}</option>)}
              </select>
            </>
          )}
          <button onClick={() => { loadAllBalances(); if (activeTab === 'orders') loadOpenOrders(); if (activeTab === 'history') loadHistory(); }}
            className="p-2.5 rounded-xl bg-dark-800 border border-dark-700 text-gray-400 hover:text-white" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'trade', label: 'Place Order', icon: Zap },
          { id: 'balances', label: 'All Balances', icon: DollarSign },
          { id: 'orders', label: 'Open Orders', icon: Clock },
          { id: 'history', label: 'Trade History', icon: BarChart2 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all ${
              activeTab === tab.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'
            }`}><tab.icon className="w-3.5 h-3.5" /> {tab.label}</button>
        ))}
      </div>

      {activeTab === 'trade' && (
        <TradingViewChart symbol={symbol} exchangeId={selectedExchange} interval="60" height={400} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Order Panel ── */}
        {activeTab === 'trade' && (
          <>
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Spot Order — {exchanges.find(e => e.exchangeId === selectedExchange)?.name}</h3>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button onClick={() => setSide('buy')} className={`py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${side === 'buy' ? 'bg-emerald-500 text-dark-900' : 'bg-dark-700 text-gray-400'}`}><ArrowUpRight className="w-4 h-4" /> Buy</button>
                <button onClick={() => setSide('sell')} className={`py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${side === 'sell' ? 'bg-red-500 text-white' : 'bg-dark-700 text-gray-400'}`}><ArrowDownRight className="w-4 h-4" /> Sell</button>
              </div>

              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5">
                    Pair
                    <InfoTip side="right">Type to search across the exchange's full markets list. Substring match — type "BTC" or "ETH/USDC" or "OP". Most-popular pairs sort to the top. Each exchange has different pairs: Binance ~2000+, Coinbase ~400, Kraken ~500.</InfoTip>
                  </span>
                  <span className="text-gray-600 text-[10px]">
                    {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} available`}
                  </span>
                </label>
                <input
                  type="text"
                  value={pairSearch}
                  onChange={e => setPairSearch(e.target.value)}
                  placeholder="Search pair (e.g. BTC, ETH/USDC)"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:outline-none mb-2"
                />
                <select
                  value={symbol}
                  onChange={e => setSymbol(e.target.value)}
                  size={Math.min(8, Math.max(3, filteredPairs.length))}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2 text-white text-sm font-mono focus:outline-none"
                >
                  {filteredPairs.length === 0 ? (
                    <option disabled>No pairs match "{pairSearch}"</option>
                  ) : (
                    filteredPairs.map(p => <option key={p} value={p}>{p}</option>)
                  )}
                </select>
                {pairs.length > 100 && (
                  <p className="text-gray-600 text-[10px] mt-1">Showing first 100 — refine search to find more</p>
                )}
              </div>

              <div className="mb-2 flex items-center justify-between">
                <span className="text-gray-500 text-xs flex items-center gap-1.5">
                  Order type
                  <InfoTip side="right">Market fills immediately at the best available price (you pay the spread). Limit sets a target price; order sits on the exchange book until filled or cancelled. Use Market for "I want this NOW"; Limit for "I want a specific price."</InfoTip>
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button onClick={() => setOrderType('market')} className={`py-2 rounded-lg text-xs font-medium ${orderType === 'market' ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>Market</button>
                <button onClick={() => setOrderType('limit')} className={`py-2 rounded-lg text-xs font-medium ${orderType === 'limit' ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>Limit</button>
              </div>

              {/* Live market price for the pair — auto-refreshes every 15s. */}
              <div className="mb-3 rounded-xl bg-dark-700/40 border border-dark-600/40 px-3 py-2 flex items-center justify-between">
                <span className="text-gray-500 text-[11px] flex items-center gap-1.5">
                  Market price
                  <InfoTip side="right">Live ticker from the selected exchange. Auto-refreshes every 15 seconds while you're on the Place Order tab. The "1 BASE = X QUOTE" reading tells you the current rate.</InfoTip>
                </span>
                {tickerLoading && !tickerPrice ? (
                  <span className="text-gray-500 text-xs flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
                ) : tickerPrice ? (
                  <span className="text-white text-xs font-mono">1 {base} = <span className="text-amber-400">{fmtQuote(tickerPrice)}</span> {quote}</span>
                ) : (
                  <span className="text-gray-600 text-xs">Price unavailable</span>
                )}
              </div>

              {/* Amount-mode toggle: enter in BASE (ETH) or QUOTE (USDC). Most
                  new users find it more intuitive to think in dollars, so we
                  expose both inputs side-by-side. */}
              <div className="mb-2 flex items-center justify-between">
                <label className="text-gray-500 text-xs flex items-center gap-1.5">
                  Enter amount in
                  <InfoTip side="right">QUOTE mode (USDT/USDC/USD) lets you type the DOLLAR amount you want to spend — recommended for your first 10 trades. BASE mode lets you type the crypto amount (0.025 ETH). The form auto-converts live so you always see both sides.</InfoTip>
                </label>
                <div className="inline-flex bg-dark-700 rounded-lg p-0.5 border border-dark-600">
                  <button onClick={() => { setAmountMode('base'); setAmount(''); }}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${amountMode === 'base' ? 'bg-amber-400 text-dark-900' : 'text-gray-500 hover:text-white'}`}>{base}</button>
                  <button onClick={() => { setAmountMode('quote'); setAmount(''); }}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${amountMode === 'quote' ? 'bg-amber-400 text-dark-900' : 'text-gray-500 hover:text-white'}`}>{quote}</button>
                </div>
              </div>

              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span>Amount ({amountMode === 'base' ? base : quote})</span>
                  {amountNum > 0 && refPrice && (
                    <span className="text-gray-600 text-[10px] font-mono">
                      ≈ {amountMode === 'base'
                          ? `${fmtQuote(quoteAmount)} ${quote}`
                          : `${fmtBase(baseAmount)} ${base}`}
                    </span>
                  )}
                </label>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} step="any" placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                {amountNum > 0 && refPrice && (
                  <p className="text-emerald-400 text-[11px] mt-1.5 font-mono">
                    {side === 'buy' ? 'You will spend' : 'You will receive'}: <span className="font-bold">{fmtQuote(quoteAmount)} {quote}</span>
                    {' '}for <span className="font-bold">{fmtBase(baseAmount)} {base}</span>
                  </p>
                )}
              </div>

              {orderType === 'limit' && (
                <div className="mb-3">
                  <label className="text-gray-500 text-xs mb-1 flex items-center gap-1.5">
                    Limit price ({quote} per {base})
                    <InfoTip side="right">The price PER UNIT you want to fill at. Order sits on the exchange book until someone fills it OR you cancel. The Open Orders tab tracks it. Placeholder shows current market price as a reference — your limit can be above (sell higher) or below (buy lower).</InfoTip>
                  </label>
                  <input type="number" value={price} onChange={e => setPrice(e.target.value)} step="any"
                    placeholder={tickerPrice ? fmtQuote(tickerPrice) : 'Limit price'}
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                </div>
              )}

              {orderResult && (
                <div className={`rounded-xl p-3 mb-3 border ${orderResult.error ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                  {orderResult.error ? <p className="text-xs text-red-400">{orderResult.error}</p> : (
                    <p className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Order placed — {orderResult.order?.side} {orderResult.order?.amount} {symbol.split('/')[0]}</p>
                  )}
                </div>
              )}

              <button onClick={handlePlaceOrder} disabled={placing || !amount}
                className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all flex flex-col items-center justify-center gap-0.5 ${
                  side === 'buy' ? 'bg-emerald-500 text-dark-900 hover:bg-emerald-400' : 'bg-red-500 text-white hover:bg-red-400'
                } disabled:opacity-40`}>
                <span className="flex items-center gap-2">
                  {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : side === 'buy' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {side === 'buy' ? 'Buy' : 'Sell'} {base} ({orderType})
                </span>
                {amountNum > 0 && refPrice && (
                  <span className="text-[10px] opacity-80 font-mono">
                    {fmtBase(baseAmount)} {base} · {fmtQuote(quoteAmount)} {quote}
                  </span>
                )}
              </button>
            </div>

            {/* Exchange info sidebar */}
            <div className="lg:col-span-2 bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" /> Connected Exchanges ({exchanges.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {exchanges.map(ex => (
                  <div key={ex.exchangeId} className={`rounded-xl p-4 border ${selectedExchange === ex.exchangeId ? 'bg-amber-400/5 border-amber-400/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold text-sm">{ex.name}</span>
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Connected</span>
                    </div>
                    <p className="text-gray-600 text-[10px] font-mono">Key: {ex.apiKeyPreview}</p>
                    <p className="text-gray-600 text-[10px]">Since: {ex.connectedAt ? new Date(ex.connectedAt).toLocaleDateString() : '—'}</p>
                    <button onClick={() => setSelectedExchange(ex.exchangeId)}
                      className={`mt-2 w-full py-1.5 rounded-lg text-[10px] font-medium ${selectedExchange === ex.exchangeId ? 'bg-amber-400 text-dark-900' : 'bg-dark-600 text-gray-400 hover:text-white'}`}>
                      {selectedExchange === ex.exchangeId ? 'Selected' : 'Switch to'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Balances Tab ── */}
        {activeTab === 'balances' && (
          <div className="lg:col-span-3">
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-400" /> Unified Balance — All Exchanges</span>
                {balLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
              </h3>
              {allBalances && (
                <>
                  <div className="bg-gradient-to-br from-amber-400/5 to-emerald-400/5 rounded-xl p-4 border border-amber-400/10 mb-4 text-center">
                    <p className="text-gray-400 text-xs">Total Portfolio Value</p>
                    <p className="text-3xl font-bold text-amber-400 font-mono">${allBalances.totalUsd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                    <p className="text-gray-600 text-xs mt-1">Across {allBalances.exchanges?.length} exchanges</p>
                  </div>
                  {allBalances.exchanges?.map(ex => (
                    <div key={ex.exchange} className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-sm capitalize">{ex.name || ex.exchange}</span>
                        <span className="text-amber-400 text-sm font-mono font-bold">${ex.totalUsd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="space-y-1">
                        {ex.balances?.slice(0, 10).map(b => (
                          <div key={b.asset} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2">
                            <span className="text-white text-xs font-medium">{b.asset}</span>
                            <div className="text-right">
                              <span className="text-white text-xs font-mono">{b.total?.toFixed(6)}</span>
                              {b.usdValue > 0 && <span className="text-gray-500 text-[10px] ml-2">${b.usdValue?.toFixed(2)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Open Orders Tab ── */}
        {activeTab === 'orders' && (
          <div className="lg:col-span-3">
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Open Orders — {exchanges.find(e => e.exchangeId === selectedExchange)?.name}</span>
                {ordersLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
              </h3>
              {openOrders.length === 0 ? (
                <div className="text-center py-8"><p className="text-gray-500 text-sm">No open orders</p></div>
              ) : (
                <div className="space-y-2">
                  {openOrders.map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${o.side === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{o.side?.toUpperCase()}</span>
                          <span className="text-white text-xs font-medium">{o.symbol}</span>
                          <span className="text-gray-600 text-[10px]">{o.type}</span>
                        </div>
                        <p className="text-gray-500 text-[10px] mt-1">Amount: {o.amount} @ ${o.price} | Filled: {o.filled}/{o.amount}</p>
                      </div>
                      <button onClick={() => handleCancelOrder(o.id)} className="px-3 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20">Cancel</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Trade History Tab ── */}
        {activeTab === 'history' && (
          <div className="lg:col-span-3">
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><BarChart2 className="w-4 h-4 text-amber-400" /> Trade History — {exchanges.find(e => e.exchangeId === selectedExchange)?.name}</span>
                {historyLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
              </h3>
              {tradeHistory.length === 0 ? (
                <div className="text-center py-8"><p className="text-gray-500 text-sm">No trade history yet</p></div>
              ) : (
                <div className="space-y-2">
                  {tradeHistory.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-dark-700/30 rounded-lg p-2.5 border border-dark-600/30">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.side === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{t.side?.toUpperCase()}</span>
                        <span className="text-white text-xs">{t.symbol}</span>
                      </div>
                      <span className="text-white text-xs font-mono">{t.amount} @ ${t.price}</span>
                      <span className="text-gray-500 text-[10px]">${t.cost?.toFixed(2)}</span>
                      <span className="text-gray-600 text-[10px]">{t.datetime ? new Date(t.datetime).toLocaleString() : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
